import { useEffect, useState } from "react";

/**
 * "View as user" toggle (admin-only feature). Stored in localStorage so
 * it survives reloads but resets across browsers. When on, the app
 * shell hides admin nav + admin-only routes; the underlying Firebase
 * Auth identity is untouched (the user is still an admin at the DB
 * level — this is a UI simulation, not a privilege drop).
 *
 * Multiple `useViewAsUser()` callers live in the tree at the same time
 * (Gate, AppShell, AdminOnly, etc.). We keep them in sync via a tiny
 * pub/sub: the storage event only fires *cross-tab*, so without the
 * subscription Gate's redirect-to-/admin can fire on stale state right
 * after the admin toggles vista usuario.
 */
const KEY = "altajet_view_as_user";
const listeners = new Set<(v: boolean) => void>();

function read(): boolean {
  if (typeof window === "undefined") return false;
  return window.localStorage.getItem(KEY) === "1";
}

function write(v: boolean) {
  if (typeof window === "undefined") return;
  if (v) window.localStorage.setItem(KEY, "1");
  else window.localStorage.removeItem(KEY);
  for (const cb of listeners) cb(v);
}

export function useViewAsUser(): [boolean, (v: boolean) => void] {
  const [on, setOn] = useState<boolean>(read);

  useEffect(() => {
    const onChange = (v: boolean) => setOn(v);
    listeners.add(onChange);
    function onStorage(e: StorageEvent) {
      if (e.key === KEY) setOn(e.newValue === "1");
    }
    window.addEventListener("storage", onStorage);
    return () => {
      listeners.delete(onChange);
      window.removeEventListener("storage", onStorage);
    };
  }, []);

  return [on, write];
}
